import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  FileText,
  Download,
  ArrowDownToLine,
  Check,
  Clock,
  CreditCard,
  FileCheck,
  ClipboardCheck,
  RotateCcw,
  Shield,
  Users,
  Eye,
  ChevronDown,
  ChevronRight,
  User,
  PenTool,
  Paperclip,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEP_META = {
  contractor_agreement: { label: 'Independent Contractor Agreement', desc: 'Contractor Agreement & Annexure A', icon: FileText, color: 'from-[#1B365D] to-[#2a4a7a]' },
  direct_deposit: { label: 'Direct Deposit Authorization', desc: 'ACH / Voided Check', icon: CreditCard, color: 'from-amber-500 to-orange-500' },
  referral_agreement: { label: 'Referral Fee Agreement', desc: 'Annexure B - Commission Policy', icon: FileCheck, color: 'from-emerald-600 to-teal-500' },
  w9: { label: 'W-9 Form', desc: 'IRS Tax Identification', icon: ClipboardCheck, color: 'from-violet-600 to-purple-500' },
  review: { label: 'Review & Submit', desc: 'Final submission', icon: Check, color: 'from-green-600 to-emerald-500' }
};

const STEP_ORDER = ['contractor_agreement', 'direct_deposit', 'referral_agreement', 'w9', 'review'];

// Admin view: all agents' onboarding statuses
const AdminOnboardingView = ({ token }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [agentDetail, setAgentDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [signatureModal, setSignatureModal] = useState(null);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/onboarding`, { headers: { Authorization: `Bearer ${token}` } });
      setAgents(res.data);
    } catch { toast.error('Failed to load onboarding data'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const toggleAgent = async (agentId) => {
    if (expandedAgent === agentId) {
      setExpandedAgent(null);
      setAgentDetail(null);
      return;
    }
    setExpandedAgent(agentId);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/onboarding/${agentId}`, { headers: { Authorization: `Bearer ${token}` } });
      setAgentDetail(res.data);
    } catch { toast.error('Failed to load agent details'); }
    finally { setDetailLoading(false); }
  };

  const handleDownloadDoc = async (agentId, step, agentName) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/onboarding/${agentId}/download/${step}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([res.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${agentName}_${step}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Downloaded ${STEP_META[step]?.label}`);
    } catch {
      toast.error('No document file available for this step');
    }
  };

  const handleViewSignature = async (agentId, step) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/onboarding/${agentId}/signature/${step}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSignatureModal({ step, ...res.data });
    } catch {
      toast.error('No signature available for this step');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-500">Loading...</div></div>;

  const completedCount = agents.filter(a => a.onboarding_completed).length;
  const inProgressCount = agents.filter(a => !a.onboarding_completed && a.completed_steps > 0).length;
  const savedCount = agents.filter(a => a.saved_for_later && !a.onboarding_completed).length;

  return (
    <div className="space-y-6" data-testid="admin-onboarding-view">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Agents', value: agents.length, color: 'text-[#1B365D]', bg: 'bg-[#1B365D]/5' },
          { label: 'Completed', value: completedCount, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'In Progress', value: inProgressCount, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Saved for Later', value: savedCount, color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map(s => (
          <Card key={s.label} className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-slate-500 mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Agent List */}
      <div className="space-y-3">
        {agents.length === 0 ? (
          <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center text-slate-500">No agents found</CardContent></Card>
        ) : agents.map(agent => {
          const isExpanded = expandedAgent === agent.agent_id;
          const pct = agent.total_steps ? Math.round((agent.completed_steps / agent.total_steps) * 100) : 0;

          return (
            <motion.div key={agent.agent_id} layout>
              <Card className={`border-0 shadow-sm transition-all ${isExpanded ? 'ring-1 ring-[#0071E3]/30 shadow-md' : 'hover:shadow-md'}`} data-testid={`agent-row-${agent.agent_id}`}>
                <CardContent className="p-0">
                  {/* Agent Header - clickable */}
                  <button
                    onClick={() => toggleAgent(agent.agent_id)}
                    className="w-full p-4 flex items-center gap-4 text-left hover:bg-slate-50/50 transition-colors"
                    data-testid={`agent-toggle-${agent.agent_id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#1B365D]/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-[#1B365D]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-slate-800 truncate">{agent.agent_name}</p>
                        {agent.onboarding_completed ? (
                          <Badge className="bg-green-100 text-green-700 text-[10px]"><Check className="w-3 h-3 mr-0.5" />Complete</Badge>
                        ) : agent.saved_for_later ? (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Clock className="w-3 h-3 mr-0.5" />Saved for Later</Badge>
                        ) : agent.completed_steps > 0 ? (
                          <Badge className="bg-blue-100 text-blue-700 text-[10px]">In Progress</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">Not Started</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{agent.agent_email}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right hidden sm:block">
                        <p className="text-xs font-medium text-slate-600">{agent.completed_steps}/{agent.total_steps} steps</p>
                        <Progress value={pct} className="h-1.5 w-24 mt-1" />
                      </div>
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    </div>
                  </button>

                  {/* Expanded Detail */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                          {detailLoading ? (
                            <div className="py-4 text-center text-sm text-slate-400 animate-pulse">Loading documents...</div>
                          ) : agentDetail ? (
                            <div className="space-y-2">
                              {STEP_ORDER.map(key => {
                                const meta = STEP_META[key];
                                const Icon = meta.icon;
                                const step = agentDetail.steps?.[key] || {};
                                const isComplete = step.status === 'completed';

                                return (
                                  <div key={key} className={`flex items-center gap-3 p-3 rounded-lg ${isComplete ? 'bg-green-50/60' : 'bg-slate-50/60'}`} data-testid={`admin-doc-${agent.agent_id}-${key}`}>
                                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${meta.color} flex items-center justify-center flex-shrink-0`}>
                                      <Icon className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-slate-700">{meta.label}</p>
                                      <div className="flex items-center gap-2 mt-0.5">
                                        {isComplete ? (
                                          <span className="text-[10px] text-green-600 font-medium flex items-center gap-0.5"><Check className="w-3 h-3" /> Completed</span>
                                        ) : step.status === 'skipped' ? (
                                          <span className="text-[10px] text-amber-600">Skipped</span>
                                        ) : (
                                          <span className="text-[10px] text-slate-400">Pending</span>
                                        )}
                                        {step.signed_at && <span className="text-[10px] text-slate-400">Signed {new Date(step.signed_at).toLocaleDateString()}</span>}
                                        {step.uploaded_at && <span className="text-[10px] text-slate-400">Uploaded {new Date(step.uploaded_at).toLocaleDateString()}</span>}
                                      </div>
                                    </div>
                                    {/* Action buttons */}
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                      {step.has_signature && (
                                        <Button
                                          variant="outline" size="sm"
                                          className="h-7 px-2 text-[11px] rounded-full"
                                          onClick={() => handleViewSignature(agent.agent_id, key)}
                                          data-testid={`view-sig-${agent.agent_id}-${key}`}
                                        >
                                          <PenTool className="w-3 h-3 mr-1" /> Signature
                                        </Button>
                                      )}
                                      {step.has_file_data && (
                                        <Button
                                          variant="outline" size="sm"
                                          className="h-7 px-2 text-[11px] rounded-full"
                                          onClick={() => handleDownloadDoc(agent.agent_id, key, agent.agent_name)}
                                          data-testid={`download-doc-${agent.agent_id}-${key}`}
                                        >
                                          <Download className="w-3 h-3 mr-1" /> Download
                                        </Button>
                                      )}
                                      {step.uploaded_file && !step.has_file_data && (
                                        <Badge variant="outline" className="text-[10px] h-6"><Paperclip className="w-3 h-3 mr-0.5" />Uploaded</Badge>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                              {agentDetail.completed_at && (
                                <p className="text-[11px] text-slate-400 pt-2 pl-1">Onboarding completed on {new Date(agentDetail.completed_at).toLocaleDateString()}</p>
                              )}
                              {agentDetail.saved_at && !agentDetail.completed && (
                                <p className="text-[11px] text-amber-500 pt-2 pl-1">Saved for later on {new Date(agentDetail.saved_at).toLocaleDateString()}</p>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-400 py-3">No onboarding data available</p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Signature Modal */}
      <AnimatePresence>
        {signatureModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => setSignatureModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl p-6 max-w-lg w-full mx-4"
              onClick={e => e.stopPropagation()}
              data-testid="signature-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800">E-Signature: {STEP_META[signatureModal.step]?.label}</h3>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSignatureModal(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="border rounded-xl p-4 bg-slate-50">
                <img src={signatureModal.signature} alt="E-Signature" className="max-w-full h-auto" />
              </div>
              {signatureModal.signed_at && (
                <p className="text-xs text-slate-500 mt-3">Signed on {new Date(signatureModal.signed_at).toLocaleString()}</p>
              )}
              <div className="mt-4 flex justify-end">
                <Button size="sm" className="rounded-full bg-[#1B365D]" onClick={() => {
                  const link = document.createElement('a');
                  link.href = signatureModal.signature;
                  link.download = `signature_${signatureModal.step}.png`;
                  link.click();
                  toast.success('Signature downloaded');
                }} data-testid="download-signature-btn">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download Signature
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Agent view: own onboarding documents
const AgentOnboardingView = ({ token }) => {
  const navigate = useNavigate();
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/onboarding/status`, { headers: { Authorization: `Bearer ${token}` } });
      setOnboarding(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-500">Loading...</div></div>;

  const steps = onboarding?.steps || {};
  const completedCount = STEP_ORDER.filter(k => steps[k]?.status === 'completed').length;
  const totalSteps = STEP_ORDER.length;
  const pct = Math.round((completedCount / totalSteps) * 100);
  const isComplete = onboarding?.completed;
  const hasPending = STEP_ORDER.some(k => k !== 'review' && steps[k]?.status !== 'completed');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="text-slate-500 mt-1">Complete, view, and download your onboarding forms</p>
        </div>
        {hasPending && !isComplete && (
          <Button onClick={() => navigate('/onboarding-wizard')} className="bg-[#0071E3] hover:bg-[#0071E3]/90 rounded-full" data-testid="resume-onboarding-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Resume Onboarding Wizard
          </Button>
        )}
      </div>

      {/* Progress Card */}
      <Card className="border-0 shadow-apple overflow-hidden">
        <div className={`h-2 bg-gradient-to-r ${isComplete ? 'from-green-500 to-emerald-500' : 'from-[#1B365D] to-[#0071E3]'}`} />
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              {isComplete ? (
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center"><Check className="w-5 h-5 text-green-600" /></div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#1B365D]/10 flex items-center justify-center"><ClipboardCheck className="w-5 h-5 text-[#1B365D]" /></div>
              )}
              <div>
                <p className="font-semibold text-slate-800">{isComplete ? 'Onboarding Complete' : 'Onboarding In Progress'}</p>
                <p className="text-xs text-slate-500">{completedCount} of {totalSteps} steps completed</p>
              </div>
            </div>
            <Badge variant={isComplete ? 'default' : 'secondary'} className={isComplete ? 'bg-green-600' : ''}>{pct}%</Badge>
          </div>
          <Progress value={pct} className="h-2" />
          {onboarding?.completed_at && <p className="text-xs text-slate-400 mt-2">Completed on {new Date(onboarding.completed_at).toLocaleDateString()}</p>}
        </CardContent>
      </Card>

      {/* Steps Status */}
      <div className="space-y-3">
        <h2 className="font-semibold text-slate-800">Your Documents</h2>
        {STEP_ORDER.map((key, idx) => {
          const meta = STEP_META[key];
          const Icon = meta.icon;
          const step = steps[key] || {};
          const isStepComplete = step.status === 'completed';
          const isSkipped = step.status === 'skipped';

          return (
            <motion.div key={key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
              <Card className={`border-0 shadow-sm transition-all ${isStepComplete ? 'ring-1 ring-green-200' : ''}`} data-testid={`doc-${key}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${meta.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-slate-800">{meta.label}</p>
                        {isStepComplete && <Badge className="bg-green-100 text-green-700 text-[10px]"><Check className="w-3 h-3 mr-0.5" />Complete</Badge>}
                        {isSkipped && <Badge variant="outline" className="text-amber-600 border-amber-200 text-[10px]"><Clock className="w-3 h-3 mr-0.5" />Pending</Badge>}
                        {!isStepComplete && !isSkipped && <Badge variant="secondary" className="text-[10px]">Not Started</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{meta.desc}</p>
                      {isStepComplete && (
                        <p className="text-[10px] text-slate-400 mt-1">
                          {step.uploaded_file ? `Uploaded: ${step.file_name || 'Document'}` : 'Signed electronically'}
                          {step.signed_at && ` · ${new Date(step.signed_at).toLocaleString()}`}
                          {step.uploaded_at && ` · ${new Date(step.uploaded_at).toLocaleString()}`}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isStepComplete && step.signature && (
                        <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={() => {
                          const link = document.createElement('a');
                          link.href = step.signature;
                          link.download = `${key}_signature.png`;
                          link.click();
                        }} data-testid={`download-sig-${key}`}>
                          <Download className="w-3 h-3 mr-1" /> Signature
                        </Button>
                      )}
                      {!isStepComplete && key !== 'review' && (
                        <Button size="sm" className="rounded-full bg-[#0071E3] hover:bg-[#0071E3]/90 text-xs" onClick={() => {
                          navigate(`/onboarding-wizard?step=${idx}`);
                        }} data-testid={`complete-${key}`}>
                          Complete Now
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Blank Forms Download */}
      <div className="space-y-3">
        <h2 className="font-semibold text-slate-800">Download Blank Forms</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-0 shadow-sm hover:shadow-md transition-all" data-testid="download-blank-w9">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Blank W-9 Form</p>
                  <p className="text-xs text-slate-500">Official IRS W-9 (PDF)</p>
                </div>
                <a href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="rounded-full"><ArrowDownToLine className="w-4 h-4 mr-1" /> Download</Button>
                </a>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm hover:shadow-md transition-all" data-testid="download-blank-contract">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1B365D] to-[#0071E3] flex items-center justify-center flex-shrink-0">
                  <ClipboardCheck className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Contractor Agreement Pack</p>
                  <p className="text-xs text-slate-500">Full onboarding forms (PDF)</p>
                </div>
                <a href="https://customer-assets.emergentagent.com/job_18c5d0c1-39eb-46ca-9d55-9c0ffabaa63c/artifacts/8br27nxk_Independent%20Contractor%20Onboarding.pdf" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="rounded-full"><ArrowDownToLine className="w-4 h-4 mr-1" /> Download</Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Security Note */}
      <Card className="border border-slate-200 bg-slate-50/50">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-[#1B365D] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-slate-800">Secure Document Handling</p>
            <p className="text-xs text-slate-500 mt-0.5">All personal information including SSN, bank details, and signatures are encrypted at rest. Your documents are only accessible to you and authorized administrators.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const OnboardingDocs = () => {
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState(isAdmin ? 'admin' : 'agent');

  return (
    <div className="space-y-6" data-testid="onboarding-docs-page">
      {/* Page Title */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-[#1B365D] tracking-tight">Onboarding Documents</h1>
        <p className="text-slate-500 mt-1">{isAdmin ? 'View and manage all agent onboarding documents' : 'Complete, view, and download your onboarding forms'}</p>
      </div>

      {/* Tab Switcher for Admins */}
      {isAdmin && (
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="onboarding-tabs">
          <button
            onClick={() => setActiveTab('admin')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'admin' ? 'bg-white shadow-sm text-[#1B365D]' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-admin-view"
          >
            <Users className="w-4 h-4" /> All Agents
          </button>
          <button
            onClick={() => setActiveTab('agent')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'agent' ? 'bg-white shadow-sm text-[#1B365D]' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-my-docs"
          >
            <Eye className="w-4 h-4" /> My Documents
          </button>
        </div>
      )}

      {/* Content */}
      {activeTab === 'admin' ? (
        <AdminOnboardingView token={token} />
      ) : (
        <AgentOnboardingView token={token} />
      )}
    </div>
  );
};

export default OnboardingDocs;
